<template>
    <transition name="fade" mode="out-in">
        <base-modal v-if="visible" :title="title" height="85vh" @close="$emit('close')">
            <div class="flex flex-col h-full min-h-0 px-8 pt-4">
                <div v-if="!loading && hasRows" class="flex justify-end mb-3">
                    <button type="button" class="dc-export-btn" :disabled="isExporting" @click.stop="exportExcel">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="dc-export-btn__icon" />
                        <span>Export Excel</span>
                    </button>
                </div>
                <div v-if="loading" class="flex flex-1 min-h-0 items-center justify-center py-16">
                    <base-spinner></base-spinner>
                </div>
                <div v-else-if="students && students.data" class="flex-1 flex flex-col min-h-0 overflow-hidden">
                    <div class="overflow-x-auto overflow-y-auto flex-1 border rounded">
                        <table class="min-w-full text-left text-sm">
                            <thead class="bg-gray-50 border-b sticky top-0">
                                <tr>
                                    <th class="px-4 py-2 text-gray-600">#</th>
                                    <th class="px-4 py-2 text-gray-600">Name</th>
                                    <th class="px-4 py-2 text-gray-600">Email</th>
                                    <th class="px-4 py-2 text-gray-600">Phone</th>
                                    <th class="px-4 py-2 text-gray-600">Student ID</th>
                                    <th class="px-4 py-2 text-gray-600">Entity</th>
                                    <th class="px-4 py-2 text-gray-600">Campus</th>
                                    <th class="px-4 py-2 text-gray-600">Program</th>
                                    <th class="px-4 py-2 text-gray-600">Current status</th>
                                    <th class="px-4 py-2 text-gray-600">Employment status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in students.data" :key="row.student_id + '-emp-dd-' + idx"
                                    class="border-b border-gray-100">
                                    <td class="px-4 py-2 whitespace-nowrap">{{ firstItem + idx }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ row.name_en }}</td>
                                    <td class="px-4 py-2">{{ studentRowEmail(row) }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ getAlumniDisplayPhone(row) || "—" }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ row.student_id }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ getEntityName(row.entity) }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ row.campus_code }}</td>
                                    <td class="px-4 py-2">{{ getSpecialization(row.entity, row.study_program, row.program_name, row.program_code, row.cluster_e) }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ row.current_status || "—" }}</td>
                                    <td class="px-4 py-2 whitespace-nowrap">{{ isEmployed(row.is_employed) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="mt-3 shrink-0">
                        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                    </div>
                </div>
                <div v-else class="flex flex-1 min-h-0 items-center justify-center py-16 text-gray-500">
                    No alumni found for this selection.
                </div>
                <base-page-spinner v-if="isExporting"></base-page-spinner>
            </div>
        </base-modal>
    </transition>
</template>

<script>
import gql from "graphql-tag";
import * as XLSX from "xlsx/dist/xlsx.full.min.js";
import Pagination from "../../../components/shared/pagination.vue";

const STUDENTS_QUERY = gql`
    query EmploymentDrilldownStudents(
        $first: Int!
        $page: Int!
        $entity: String
        $student_status: String
        $campuses: [String]
        $local_or_non_local: String
        $gender: String
        $academic_years: [String]
        $is_employed: String
        $is_employed_values: [String]
        $employer: String
        $specialization: String
        $eligible_for_employment: Int
        $got_job_within: String
        $got_job_within_list: [String]
        $unemployed_reason: String
        $employment_source: [String]
        $is_job_rel_major: String
    ) {
        students(
            first: $first
            page: $page
            entity: $entity
            student_status: $student_status
            campuses: $campuses
            local_or_non_local: $local_or_non_local
            gender: $gender
            academic_years: $academic_years
            is_employed: $is_employed
            is_employed_values: $is_employed_values
            employer: $employer
            specialization: $specialization
            eligible_for_employment: $eligible_for_employment
            got_job_within: $got_job_within
            got_job_within_list: $got_job_within_list
            unemployed_reason: $unemployed_reason
            employment_source: $employment_source
            is_job_rel_major: $is_job_rel_major
        ) {
            data {
                name_en
                student_id
                entity
                campus_code
                program_code
                program_name
                study_program
                cluster_e
                primary_email
                secondary_email
                alumni_phone
                primary_phone
                secondary_phone
                third_phone
                fourth_phone
                father_phone_number
                mother_phone_number
                user {
                    email
                }
                is_employed
                current_status
            }
            paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
            }
        }
    }
`;

const EXPORT_QUERY = gql`
    query EmploymentDrilldownExport(
        $page: Int
        $limit: Int
        $entity: String
        $student_status: String
        $campuses: [String]
        $local_or_non_local: String
        $gender: String
        $academic_years: [String]
        $is_employed: String
        $is_employed_values: [String]
        $employer: String
        $specialization: String
        $eligible_for_employment: Int
        $got_job_within: String
        $got_job_within_list: [String]
        $unemployed_reason: String
        $employment_source: [String]
        $is_job_rel_major: String
    ) {
        generateStudentsReport(
            page: $page
            limit: $limit
            entity: $entity
            student_status: $student_status
            campuses: $campuses
            local_or_non_local: $local_or_non_local
            gender: $gender
            academic_years: $academic_years
            is_employed: $is_employed
            is_employed_values: $is_employed_values
            employer: $employer
            specialization: $specialization
            eligible_for_employment: $eligible_for_employment
            got_job_within: $got_job_within
            got_job_within_list: $got_job_within_list
            unemployed_reason: $unemployed_reason
            employment_source: $employment_source
            is_job_rel_major: $is_job_rel_major
        ) {
            error
            error_code
            message
            data {
                name_en
                student_id
                entity
                campus_code
                program_code
                program_name
                study_program
                cluster_e
                primary_email
                secondary_email
                alumni_phone
                primary_phone
                secondary_phone
                third_phone
                fourth_phone
                father_phone_number
                mother_phone_number
                is_employed
                current_status
            }
        }
    }
`;

export default {
    name: "EmploymentDrilldownModal",
    components: {
        Pagination: Pagination,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: "Alumni Details",
        },
        fileName: {
            type: String,
            default: "Employment_Drilldown",
        },
        baseFilters: {
            type: Object,
            default: function () {
                return {};
            },
        },
        overrides: {
            type: Object,
            default: function () {
                return {};
            },
        },
    },
    emits: ["close"],
    data: function () {
        return {
            loading: false,
            isExporting: false,
            students: null,
            pageSize: 10,
        };
    },
    computed: {
        queryVariables: function () {
            var merged = Object.assign({}, this.baseFilters || {}, this.overrides || {});
            var cleaned = {};
            Object.keys(merged).forEach(function (key) {
                var val = merged[key];
                if (val === undefined || val === null || val === "") {
                    return;
                }
                if (Array.isArray(val) && val.length === 0) {
                    return;
                }
                cleaned[key] = val;
            });
            return cleaned;
        },
        hasRows: function () {
            if (!this.students) {
                return false;
            }
            if (this.students.paginatorInfo && this.students.paginatorInfo.total > 0) {
                return true;
            }
            return !!(this.students.data && this.students.data.length);
        },
        paginatorInfo: function () {
            if (this.students && this.students.paginatorInfo) {
                return this.students.paginatorInfo;
            }
            return {
                count: 0,
                currentPage: 1,
                firstItem: 0,
                hasMorePages: false,
                lastItem: 0,
                lastPage: 1,
                perPage: this.pageSize,
                total: 0,
            };
        },
        firstItem: function () {
            var info = this.paginatorInfo;
            return info && info.firstItem ? info.firstItem : 1;
        },
    },
    watch: {
        visible: function (isVisible) {
            if (isVisible) {
                this.fetchStudents(1);
            } else {
                this.students = null;
            }
        },
        overrides: {
            deep: true,
            handler: function () {
                if (this.visible) {
                    this.fetchStudents(1);
                }
            },
        },
    },
    methods: {
        setPage: function (page) {
            this.fetchStudents(page);
        },
        async fetchStudents(pageNo) {
            this.loading = true;
            try {
                var response = await this.$apollo.query({
                    query: STUDENTS_QUERY,
                    variables: Object.assign(
                        {
                            first: this.pageSize,
                            page: pageNo || 1,
                        },
                        this.queryVariables
                    ),
                    fetchPolicy: "network-only",
                });
                this.students = response.data.students;
            } catch (error) {
                console.error("Failed to load employment drilldown students:", error);
                this.students = null;
            }
            this.loading = false;
        },
        async fetchForExporting(pageNo, limitSize) {
            var result = await this.$apollo.query({
                query: EXPORT_QUERY,
                variables: Object.assign(
                    {
                        page: pageNo,
                        limit: limitSize,
                    },
                    this.queryVariables
                ),
                fetchPolicy: "network-only",
            });
            return result && result.data ? result.data.generateStudentsReport : null;
        },
        async exportExcel() {
            this.isExporting = true;
            try {
                var rows = [];
                var page = 1;
                var limit = 200;
                var hasMore = true;
                while (hasMore) {
                    var payload = await this.fetchForExporting(page, limit);
                    var batch = payload && payload.data ? payload.data : [];
                    for (var i = 0; i < batch.length; i++) {
                        rows.push(this.buildExportRow(batch[i]));
                    }
                    hasMore = batch.length === limit;
                    page = page + 1;
                }
                if (!rows.length) {
                    this.isExporting = false;
                    return;
                }
                var worksheet = XLSX.utils.json_to_sheet(rows);
                var headers = this.getExportHeaders();
                for (var h = 0; h < headers.length; h++) {
                    worksheet[this.getColumnLetter(h + 1) + "1"] = {
                        t: "s",
                        v: headers[h],
                    };
                }
                worksheet["!cols"] = this.getExportColWidths();
                var workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Alumni");
                var safeName = String(this.fileName || "Employment_Drilldown").replace(/[^\w-]+/g, "_");
                XLSX.writeFile(workbook, safeName + ".xlsx");
            } catch (error) {
                console.error("Failed to export employment drilldown:", error);
            }
            this.isExporting = false;
        },
        buildExportRow: function (obj) {
            return {
                name_en: obj.name_en,
                phone: this.getAlumniDisplayPhone(obj),
                primary_email: obj.primary_email || "",
                secondary_email: obj.secondary_email || "",
                student_id: obj.student_id,
                entity: this.getEntityName(obj.entity),
                campus: obj.campus_code,
                program: this.getSpecialization(
                    obj.entity,
                    obj.study_program,
                    obj.program_name,
                    obj.program_code,
                    obj.cluster_e
                ),
                current_status: obj.current_status || "",
                employment_status: this.isEmployed(obj.is_employed),
            };
        },
        getExportHeaders: function () {
            return [
                "NAME",
                "PHONE",
                "SCHOOL/COLLEGE EMAIL",
                "PERSONAL EMAIL",
                "STUDENT ID",
                "ENTITY",
                "CAMPUS",
                "PROGRAM",
                "CURRENT STATUS",
                "EMPLOYMENT STATUS",
            ];
        },
        getExportColWidths: function () {
            return [
                { width: 30 },
                { width: 18 },
                { width: 36 },
                { width: 36 },
                { width: 18 },
                { width: 14 },
                { width: 12 },
                { width: 40 },
                { width: 28 },
                { width: 22 },
            ];
        },
        getColumnLetter: function (columnNumber) {
            var result = "";
            var n = columnNumber;
            while (n > 0) {
                n = n - 1;
                result = String.fromCharCode(65 + (n % 26)) + result;
                n = Math.floor(n / 26);
            }
            return result;
        },
        studentRowEmail: function (row) {
            if (!row) {
                return "—";
            }
            if (row.primary_email) {
                return row.primary_email;
            }
            if (row.user && row.user.email) {
                return row.user.email;
            }
            if (row.secondary_email) {
                return row.secondary_email;
            }
            return "—";
        },
        getAlumniDisplayPhone: function (row) {
            if (!row) {
                return "";
            }
            var fields = [
                "alumni_phone",
                "primary_phone",
                "secondary_phone",
                "third_phone",
                "fourth_phone",
                "father_phone_number",
                "mother_phone_number",
            ];
            for (var i = 0; i < fields.length; i++) {
                var value = row[fields[i]];
                if (value !== undefined && value !== null && String(value).trim() !== "") {
                    return String(value).trim();
                }
            }
            return "";
        },
        getEntityName: function (entity) {
            if (entity == "ATHS") {
                return "ATS";
            }
            return entity;
        },
        getSpecialization: function (entity, studyProgram, programName, programCode, clusterE) {
            if (entity == "ATHS") {
                if (clusterE) {
                    return clusterE;
                }
                if (programName) {
                    return programName;
                }
                if (studyProgram) {
                    return studyProgram;
                }
                return programCode || "";
            }
            if (programName) {
                return programName;
            }
            if (studyProgram) {
                return studyProgram;
            }
            return programCode || "";
        },
        isEmployed: function (value) {
            if (value === null || value === undefined) {
                return "Not Contacted";
            }
            if (value === true || value === 1 || value === "1") {
                return "Employed";
            }
            return "Unemployed";
        },
    },
};
</script>

<style lang="scss" scoped>
.dc-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.dc-export-btn:hover {
    border-color: rgba(16, 185, 129, 0.45);
    background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.dc-export-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.dc-export-btn__icon {
    font-size: 1.05rem;
    color: #059669;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
